'use client'

import React, { Suspense } from 'react'
import { Provider } from 'react-redux'
import { ChakraProvider, ColorModeScript } from '@chakra-ui/react'
import { Toaster, resolveValue } from 'react-hot-toast'
import { QueryClient, keepPreviousData, QueryClientProvider } from '@tanstack/react-query'

import { makeStore } from '@/lib/redux/store'
import theme from './theme'
import { useSentryRoutePerf } from '@/lib/hooks/useSentryRoutePerf'
import ConstantsLoader from '../features/constants/ConstantsLoader'

type Props = { children: React.ReactNode }

// useSentryRoutePerf() reads useSearchParams(), which requires a Suspense
// boundary or it bails routes out of static prerendering entirely.
function RoutePerf() {
  useSentryRoutePerf()
  return null
}

export function Providers({ children }: Props) {
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: 2, // how many times to retry a failed query
            refetchOnWindowFocus: false, // don’t refetch every time a window refocuses
            staleTime: 1000 * 60 * 5, // data stays fresh for 5 mins
            gcTime: 10 * 60 * 1000,
            refetchOnReconnect: true, // refetch when the browser reconnects
            structuralSharing: true, // use structural sharing for query results
            placeholderData: keepPreviousData, // keep previous data while fetching new data
          },
          mutations: {
            retry: false, // retry mutations once on failure
          },
        },
      })
  )

  const store = React.useMemo(() => makeStore({ user: { data: null } }), [])

  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <Suspense fallback={null}>
          <RoutePerf />
        </Suspense>
        <ColorModeScript initialColorMode={theme.config.initialColorMode} />
        <ChakraProvider>
          {/* ConstantsLoader handles constants-related side effects post-hydration without affecting SSR. */}
          <ConstantsLoader />
          {children}
        </ChakraProvider>
        <Toaster
          containerStyle={{
            bottom: 40,
            left: 20,
            right: 20,
          }}
          position="bottom-center"
          gutter={10}
          toastOptions={{
            duration: 2600,
          }}
        >
          {(t) => (
            // Enters up 12px on the strong ease-out; exits faster with a slight shrink so the
            // stack feels like it settles rather than swaps. Transform/opacity only.
            <div
              style={{
                opacity: t.visible ? 1 : 0,
                transform: t.visible ? 'translateY(0) scale(1)' : 'translateY(12px) scale(0.98)',
                transition: t.visible
                  ? 'transform 240ms var(--ease-out-strong), opacity 240ms var(--ease-out-strong)'
                  : 'transform 160ms var(--ease-out-strong), opacity 160ms var(--ease-out-strong)',
                willChange: 'transform, opacity',
              }}
            >
              {resolveValue(t.message, t)}
            </div>
          )}
        </Toaster>
      </Provider>
    </QueryClientProvider>
  )
}
